import { useTranslation } from 'react-i18next';
import styles from './SidebarLayout.module.scss';
import Icon from '../Icon';
import { Text } from '../typography';
import { keys } from '../../utilities/translator/translation_keys';
import { customColors } from '../../utilities';
import Dropdown from '../dropdownV2/Dropdown';
import Button from '../Button';
import constants from '../../styles/contants.module.scss';
import UserSelector from '../UserSelector';
import Label from '../action_items/Label';
import { useState } from 'react';
import SimpleUserCard from '../SimpleUserCard';
import UserMultiSelector from '../UserMultiSelector';
import DefaultDateSelector from '../DefaultDateSelector';
import SimpleWorkspaceCard from '../SimpleWorkspaceCard';
import { Form } from 'react-bootstrap';
import UniversalDropdown from '../dropdownV2/UniversalDropdown';

export default function SidebarLayout({
  options = [],
  readOnly = false,
  buttons = [],
  switches = [],
  navigationComponent = null,
  topComponent = null,
  clearable = false,
}) {
  const { t } = useTranslation();

  const [editing, setEditing] = useState(null);

  function getComponent(option) {
    if (readOnly && !!option.value && option.type !== 'date') {
      return <Text noMargin>{option.value}</Text>;
    }
    switch (option.type) {
      case 'dropdown':
        return (
          <UniversalDropdown
            {...option}
            readOnly={readOnly}
            selected={option.selected}
          />
        );
      case 'user':
        return option.editable ? (
          <UserSelector readOnly={readOnly} {...option} />
        ) : (
          <SimpleUserCard
            mediumAvatar
            weight="regular"
            {...option}
            smallText={option.text || ''}
            smallTextSize="xs"
          />
        );
      case 'multiuser':
        if (readOnly) {
          return option.selected?.length ? (
            <div className={styles.userList}>
              {option.selected.map((user) => (
                <SimpleUserCard
                  key={user.id}
                  user={user}
                  mediumAvatar
                  weight="regular"
                />
              ))}
            </div>
          ) : (
            <Text noMargin>{t(keys.common.NONE)}</Text>
          );
        }
        return (
          <UserMultiSelector
            readOnly={readOnly}
            selected={option.selected}
            {...option}
            type={option.showUsers}
          />
        );
      case 'label':
        return (
          <Label
            rowSized={false}
            name={option.name}
            backgroundColor={option.color}
          />
        );
      case 'link':
        return (
          <Text weight="semiBold" color="accentPrimary" noMargin>
            {option.value}
          </Text>
        );
      case 'date':
        return readOnly ? (
          <Text noMargin>{option.date}</Text>
        ) : (
          <DefaultDateSelector
            className={styles.date}
            onChange={option.onChange}
            value={option.value}
            min={option?.min}
            clearable={option.clearable}
          />
        );
      case 'workspace':
        return readOnly ? (
          <SimpleWorkspaceCard weight="regular" workspace={option.selected} />
        ) : (
          <Dropdown type="workspace" {...option} />
        );
      case 'text':
        return readOnly || option.editable === false ? (
          <Text noMargin>{option.value}</Text>
        ) : (
          <Form.Control {...option} />
        );
      default:
        return <Text noMargin>{option.value}</Text>;
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text size="lg" weight="bold" noMargin>
          {t(keys.common.INFORMATION)}
        </Text>
        {navigationComponent}
      </div>
      <div className={styles.section}> {topComponent}</div>
      {options?.map((o) => {
        if (o.show === false) return null;
        const component = getComponent(o);
        return (
          <div className={styles.section} key={o.key}>
            <div className={styles.editable}>
              <Text weight="semiBold" noMargin>
                {o.title}
                {!!o.required ? (
                  <span style={{ color: customColors.RED }}>*</span>
                ) : (
                  ''
                )}
              </Text>
              {!!o.editable && !readOnly ? (
                <Icon
                  hover
                  style={{
                    color: editing
                      ? customColors.GREEN
                      : customColors.SECONDARY,
                    fontSize: '1.2rem',
                  }}
                  onClick={() => {
                    setEditing(editing === o.key ? null : o.key);
                  }}
                >
                  {editing === o.key ? 'check' : 'settings'}
                </Icon>
              ) : null}
            </div>
            <div>{component}</div>
          </div>
        );
      })}
      {switches?.length ? (
        <div className={styles.switches}>
          {switches.map((s) => {
            if (!s.show) return null;
            return (
              <Form.Check
                key={s.field}
                type="switch"
                {...s}
                onChange={(e) => s.onChange(e, s.field)}
              />
            );
          })}
        </div>
      ) : null}
      {buttons?.length ? (
        <div className={styles.buttons}>
          {buttons.map((b) => {
            if (!b.show) return null;
            return (
              <Button key={b.value} className={constants.fullWidth} {...b} />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
